<template>
  <div class="lhs-header-banner">
    <div class="lhs-header-banner__avatar-btn" :title="displayName">
      <span class="lhs-header-banner__avatar">
        <img v-if="avatarUrl" :src="avatarUrl" :alt="displayName" />
        <span v-else class="lhs-header-banner__initials" :style="initialsStyle">{{ initials }}</span>
      </span>
    </div>

    <div class="lhs-header-banner__identity">
      <div class="lhs-header-banner__name-row">
        <router-link :to="profileRoute" class="lhs-header-banner__name" :title="displayName">{{ displayName }}</router-link>
        <button
          ref="menuTriggerEl"
          type="button"
          class="lhs-header-banner__menu-trigger"
          :class="{ 'is-open': menuOpen }"
          aria-label="Account menu"
          @click="toggleMenu"
        >
          <CygnusIcon name="chevron-down" />
        </button>
      </div>
      <div v-if="resolvedPageTitle" class="lhs-header-banner__page-title" :title="resolvedPageTitle">{{ resolvedPageTitle }}</div>
      <div v-else-if="jobTitle" class="lhs-header-banner__job-title" :title="jobTitle">{{ jobTitle }}</div>
    </div>

    <div class="lhs-header-banner__ws-row">
      <slot name="ws-row">
        <!-- Agency Phase 1 — the workspace switcher. It renders ONLY when the
             user belongs to more than one workspace; a single-workspace user
             sees nothing here, so the rail is byte-identical to before. Data
             comes from /shell/me (workspaces + active_workspace). -->
        <workspace-switcher :workspaces="workspaces" :active-tenant-id="activeTenantId" />
      </slot>
    </div>

    <teleport to="body">
      <div
        v-if="menuOpen"
        class="lhs-header-banner__menu-backdrop"
        @click.self="closeMenu"
      >
        <div class="lhs-header-banner__menu-panel" :style="menuPanelStyle" role="menu">
          <router-link :to="profileRoute" class="lhs-header-banner__menu-item" @click="closeMenu">
            <CygnusIcon name="user" class="me-2" /> My profile
          </router-link>

          <!-- Theme toggle — light (default) / dark "ink". Persisted per-device;
               the menu stays open so the flip is visible immediately. -->
          <button type="button" class="lhs-header-banner__menu-item" @click="onToggleTheme">
            <svg v-if="currentTheme === 'dark'" class="cygnus-icon me-2" viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg><svg v-else class="cygnus-icon me-2" viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
            {{ currentTheme === 'dark' ? 'Light mode' : 'Dark mode' }}
          </button>

          <!-- Consumer-extended menu items. Slot scope exposes `closeMenu` so
               clicked items can dismiss the popover. -->
          <slot name="menu-items" :close-menu="closeMenu" :user="user" />

          <div class="lhs-header-banner__menu-divider"></div>
          <a href="#" class="lhs-header-banner__menu-item" @click.prevent="doLogout">
            <CygnusIcon name="sign-out" class="me-2" /> Logout
          </a>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
/**
 * LhsHeaderCard (icarus-theme-base) — top-of-LHS account banner.
 *
 * Contains: avatar (initials fallback when no upload exists), display name
 * linking to /profile, optional page title (from prop or `route.meta.lhsTitle`),
 * a slot for the workspace pill (consumer mounts WorkspaceSwitcher etc.),
 * and a chevron user-menu popover with My Profile + slot-supplied items
 * + Logout.
 *
 * The standalone TC LhsHeaderCard ships richer features (avatar upload modal,
 * page-layout edit shortcut, recurring-meetings / calendar-connections links
 * etc.) — those depend on TC-only components/endpoints and don't belong in
 * the cross-Cygnus base. Consumers add them via the `menu-items` slot.
 *
 * Endpoints overridable via props for projects with non-standard URLs.
 */
import useApi from '@apexl/icarus-core/src/services/api';
// Theme toggle — the shared service (icarus/src/services/theme.js) owns the
// single write path (localStorage + server mirror, F-OB-03). Previously this
// component duplicated the localStorage-only logic inline, which meant the
// rail's toggle button never actually reached the server-sync code added to
// applyTheme()/toggleTheme() — routing through the service fixes that.
import { getStoredTheme, toggleTheme } from '@root/services/theme';

export default {
  name: 'LhsHeaderCard',
  props: {
    /** Endpoint returning `{ user: {...}, profile: {...} }`. Default matches Cygnus shell module convention. */
    meEndpoint:      { type: String, default: '/api/v1/shell/me' },
    /** Logout endpoint — invalidates JWT server-side. Vendor convention. */
    logoutEndpoint:  { type: String, default: '/api/v1/user/action/logout' },
    /** Route the avatar + name link to. */
    profileRoute:    { type: String, default: '/profile' },
    /** URL after logout. Hard nav (not router push) to tear the JS VM down. */
    postLogoutUrl:   { type: String, default: '/login' },
    /**
     * Optional explicit page title. If empty, falls back to
     * `route.meta.lhsTitle` (set by your router's afterEach).
     */
    pageTitle:       { type: String, default: '' },
    /**
     * Map an `avatar_file_id` → URL. Default targets the apexl/io-file
     * vendor's serve endpoint. Override if your project uses a different
     * file-serving convention.
     */
    avatarServeUrl:  { type: Function, default: (fid) => `/api/v1/files/${encodeURIComponent(fid)}/serve` },
    /**
     * F-RN-05 — display-only current-workspace row. Reuses the EXISTING
     * tenant-scoped company endpoint (AdminVoiceGoals.vue already reads it)
     * rather than inventing a new one. Note: as of this pass the endpoint's
     * whitelisted fields are company_number/status/website/sector/size/
     * sells/tone_of_voice/business_goals — no `name` — so `workspaceName`
     * stays empty and the row stays hidden until that field is added
     * server-side. Wired defensively now so it lights up the moment it is.
     */
    companyEndpoint: { type: String, default: '/api/v1/me/company' },
  },

  data() {
    return {
      userId:    0,
      firstName: '',
      lastName:  '',
      jobTitle:  '',
      avatarFid: null,
      isAdmin:   false,
      menuOpen:  false,
      menuAnchor: null,
      currentTheme: getStoredTheme(),
      // Agency Phase 1 — active workspaces for the switcher (from /shell/me).
      workspaces: [],
      activeTenantId: 0,
    };
  },

  computed: {
    user() {
      return {
        id: this.userId,
        firstName: this.firstName,
        lastName: this.lastName,
        jobTitle: this.jobTitle,
        isAdmin: this.isAdmin,
      };
    },

    displayName() {
      const parts = [this.firstName, this.lastName].filter(Boolean);
      return parts.join(' ') || 'My profile';
    },

    initials() {
      const f = this.firstName?.[0] || '';
      const l = this.lastName?.[0]  || '';
      return (f + l).toUpperCase() || '?';
    },

    initialsStyle() {
      // Deterministic colour per name — same colour every session.
      let h = 0;
      const s = this.displayName || '';
      for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      const hue = Math.abs(h) % 360;
      return { backgroundColor: `hsl(${hue}, 45%, 78%)`, color: `hsl(${hue}, 35%, 28%)` };
    },

    avatarUrl() {
      return this.avatarFid ? this.avatarServeUrl(this.avatarFid) : null;
    },

    resolvedPageTitle() {
      if (this.pageTitle) return this.pageTitle;
      return this.$route?.meta?.lhsTitle || '';
    },

    // The card sits at the TOP of the rail, under the logo (Toby 2026-07-15 —
    // the mockup's foot placement was tried and reverted), so the panel opens
    // DOWNWARD from the trigger's bottom edge.
    menuPanelStyle() {
      const a = this.menuAnchor;
      if (!a) return {};
      return {
        position: 'fixed',
        top: `${Math.min(window.innerHeight - 8, a.bottom + 6)}px`,
        left: `${Math.max(8, a.left)}px`,
        minWidth: `${Math.max(220, a.width + 24)}px`,
      };
    },
  },

  async mounted() {
    await this.load();
  },

  methods: {
    async load() {
      try {
        const body = await useApi().get(this.meEndpoint);
        const u = body?.user || {};
        const p = body?.profile || {};
        this.userId    = u.id || 0;
        this.firstName = u.first_name || '';
        this.lastName  = u.last_name  || '';
        this.jobTitle  = p.job_title || '';
        this.avatarFid = p.avatar_file_id || null;
        this.isAdmin   = !!u.is_admin;
        // Agency Phase 1 — workspaces + active workspace drive the switcher.
        this.workspaces     = Array.isArray(body?.workspaces) ? body.workspaces : [];
        this.activeTenantId = body?.active_workspace?.tenant_id || 0;
      } catch (_) {
        // Non-fatal — initials + "My profile" fallback render fine.
      }
    },

    /**
     * Logout: GET the vendor endpoint to invalidate the JWT server-side, then
     * full-page-redirect (NOT router.push) — tearing down the JS VM wipes
     * Pinia stores + any in-flight axios state. Important on shared machines.
     * Endpoint returns JSON, so a bare <a href> would render the JSON blob;
     * the explicit GET avoids that.
     */
    async doLogout() {
      this.closeMenu();
      try { await useApi().get(this.logoutEndpoint); }
      catch (_) { /* token may already be dead — proceed anyway */ }
      window.location.href = this.postLogoutUrl;
    },

    onToggleTheme() {
      // toggleTheme() (icarus/src/services/theme.js) reflects + persists
      // locally AND fire-and-forgets a PUT to /api/v1/shell/me/preferences
      // (F-OB-03) — single write path, keep this component out of the
      // localStorage/data-theme business entirely.
      this.currentTheme = toggleTheme();
    },

    toggleMenu() {
      if (this.menuOpen) { this.closeMenu(); return; }
      const el = this.$refs.menuTriggerEl;
      if (el?.getBoundingClientRect) {
        const r = el.getBoundingClientRect();
        this.menuAnchor = { top: r.top, bottom: r.bottom, left: r.left, width: r.width };
      }
      this.menuOpen = true;
    },
    closeMenu() { this.menuOpen = false; this.menuAnchor = null; },
  },
};
</script>

<style scoped lang="scss">
.lhs-header-banner {
  display: grid;
  grid-template-columns: 44px 1fr;
  grid-template-rows: auto auto;
  column-gap: 0.7rem;
  row-gap: 0.5rem;
  padding: 0.7rem 0.85rem 0.55rem;
  background: var(--theme-rail-zone-bg);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid var(--theme-rail-zone-border);

  &__avatar-btn {
    grid-row: 1 / 2;
    grid-column: 1 / 2;
    background: none; border: none; padding: 0;
    position: relative; line-height: 0;
  }

  &__avatar {
    display: inline-flex; align-items: center; justify-content: center;
    width: 40px; height: 40px; border-radius: 50%;
    overflow: hidden;
    background: var(--theme-bg-card, #F5F1E9);
    border: 2px solid var(--theme-rail-zone-border);
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);

    img { width: 100%; height: 100%; object-fit: cover; }
  }

  &__initials {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.9rem; font-weight: 700; user-select: none;
  }

  &__identity {
    grid-row: 1 / 2; grid-column: 2 / 3;
    display: flex; flex-direction: column; min-width: 0;
    align-self: center;
  }

  &__name-row {
    display: flex; align-items: center; gap: 0.4rem; min-width: 0;
  }

  &__name {
    color: var(--theme-rail-text-strong);
    font-weight: 600; font-size: 0.88rem; line-height: 1.1;
    text-decoration: none;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    flex: 1 1 auto; min-width: 0;
    &:hover { color: var(--theme-accent-primary); }
  }

  &__menu-trigger {
    flex-shrink: 0;
    width: 22px; height: 22px; padding: 0;
    border-radius: 4px;
    border: 1px solid var(--theme-rail-hairline);
    background: transparent;
    color: var(--theme-text-sidebar);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.7rem;
    transition: background-color 100ms, color 100ms, transform 150ms;

    &:hover { background: var(--theme-rail-hover-bg); color: var(--theme-rail-text-strong); }
    &.is-open {
      transform: rotate(180deg);
      background: var(--theme-rail-active-bg);
      color: var(--theme-text-sidebar-active);
    }
  }

  &__page-title {
    color: var(--theme-rail-text-subtle);
    font-family: var(--theme-font-display, system-ui);
    font-size: 0.72rem; line-height: 1.2; margin-top: 0.15rem;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    letter-spacing: 0.02em;
  }

  &__job-title {
    color: var(--theme-rail-text-muted);
    font-size: 0.7rem; line-height: 1.2; margin-top: 0.15rem;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  &__ws-row {
    grid-row: 2 / 3;
    grid-column: 1 / 3;
    padding: 0;
    // Empty when no ws-row slot supplied. Consumer mounts their pill here.
    &:empty { display: none; }
  }

  // F-RN-05 — display-only current-workspace label (static, no switcher).
  &__workspace {
    display: flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 500;
    color: var(--theme-rail-text-subtle);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    i { opacity: 0.75; font-size: 0.66rem; }
  }
}
</style>

<style lang="scss">
/* Modal + menu styles are NOT scoped — `<teleport to="body">` moves them
   outside this component's tree. Class names are namespaced. */
.lhs-header-banner__menu-backdrop {
  position: fixed; inset: 0; z-index: 2040;
}
.lhs-header-banner__menu-panel {
  background: var(--theme-bg-card, #fff);
  border-radius: var(--theme-radius-lg, 8px);
  box-shadow: var(--theme-shadow-panel, 0 8px 28px rgba(0, 0, 0, 0.2));
  border: 1px solid var(--theme-border, #E8E3DC);
  padding: 0.4rem 0;
  z-index: 2050;
}
.lhs-header-banner__menu-item {
  display: flex; align-items: center;
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
  color: var(--theme-text-primary, #2A2B2E);
  text-decoration: none;
  white-space: nowrap;

  // Support <button> menu items (theme toggle) alongside links.
  width: 100%;
  border: 0;
  background: none;
  text-align: left;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: var(--theme-accent-light, #F5F1E9);
    color: var(--theme-text-primary, #2A2B2E);
  }
  i { color: var(--theme-accent-primary, #8E7456); width: 18px; text-align: center; }
}
.lhs-header-banner__menu-divider {
  height: 1px;
  background: var(--theme-border, #E8E3DC);
  margin: 0.35rem 0;
}
</style>
